import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';
import { Paginator } from '../paginator/Paginator';

import { FileSelection, UpdateFileNamePayload } from './fileUploadState';
import { UploadFile } from './UploadFile';
import { FileData, isFileSelection } from './uploadTypes';

import styles from './upload-file-section.module.scss';

type FileSectionProps = {
  isNameEditable?: boolean;
  files: FileData[];
  onFileClicked?: (id: string, file: FileData) => void;
  onRemoveFile: (file: FileData) => void;
  onFileNameChange?: (value: UpdateFileNamePayload) => void;
  selectedFileId?: string;
  showPagination?: boolean;
  showUploadState: boolean;
  title: string;
  uploadFile?: (file: FileSelection) => Promise<void>;
  metaDataInputLabel?: string;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
  withPreview?: boolean;
};

// we offer only up to 50 items per page in this view, too many items can deteriorate performance
const pageSizes = [12, 25, 50];

export const FileSection = ({
  isNameEditable,
  files,
  onFileClicked,
  onRemoveFile,
  onFileNameChange,
  selectedFileId,
  showPagination = false,
  showUploadState,
  title,
  uploadFile,
  metaDataExtractionInput,
  metaDataInputLabel,
  withPreview = false,
}: FileSectionProps) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSizes[0]);

  const sortedFiles = useMemo(() => {
    // Map files with their original indices to maintain order
    const filesWithIndex = files.map((file, index) => ({ file, index }));

    const sorted = filesWithIndex.sort((a, b) => {
      const fileA = a.file;
      const fileB = b.file;

      if (!(isFileSelection(fileA) && isFileSelection(fileB))) {
        // Preserve order for non-file selections
        return a.index - b.index;
      } else if (fileA.uploadState === fileB.uploadState) {
        // Same state: preserve original order using index
        return a.index - b.index;
      } else if (fileA.uploadState === 'isSuccess') {
        // Move isSuccess files to the end, but maintain order among them
        return 1;
      } else if (fileB.uploadState === 'isSuccess') {
        // Move isSuccess files to the end, but maintain order among them
        return -1;
      }

      // Different states but neither is isSuccess: preserve original order
      return a.index - b.index;
    });

    // Extract just the files from the sorted array
    const sortedFilesOnly = sorted.map((item) => item.file);

    if (showPagination) {
      return sortedFilesOnly.slice(
        currentPage * currentPageSize,
        currentPage * currentPageSize + currentPageSize
      );
    }
    return sortedFilesOnly;
  }, [currentPage, currentPageSize, files, showPagination]);

  if (sortedFiles.length === 0) {
    return null;
  }

  return (
    <div className={styles['root']}>
      <div className={styles['scroll-root']}>
        <div className={styles['header-container']}>
          <div className={styles['header-title']}>{title}</div>
          <div className={styles['header-divider']}></div>
          <div
            className={clsx(styles['header-summary'], {
              [styles['header-upload']]: showUploadState,
            })}
          >
            {showUploadState
              ? `${files.reduce((count, current) => {
                  if (
                    isFileSelection(current) &&
                    current.uploadState === 'isSuccess'
                  ) {
                    return count + 1;
                  }
                  return count;
                }, 0)}/${files.length}`
              : t('app.upload.fileSummary', '', {
                  count: files.length,
                  totalFileSize: fileSizeFormatter(
                    files.reduce((prev, curr) => prev + curr.file.size, 0)
                  ),
                })}
          </div>
        </div>

        {metaDataExtractionInput && (
          <div className={styles['meta-data-table']}>
            <div className={styles['meta-data-header']}>
              <div className={styles['meta-data-column']}>
                {t('app.upload.extractMetaData.fileName')}
              </div>
              <div className={styles['meta-data-column']}>
                {metaDataInputLabel}
              </div>
              <div className={styles['meta-data-column']}>
                {t('app.upload.extractMetaData.size')}
              </div>
            </div>
          </div>
        )}
        <div
          className={clsx(styles['meta-data-rows'], {
            [styles['meta-data-rows__with-preview']]: withPreview,
          })}
        >
          {sortedFiles.map((fileData, index) => {
            const key =
              'id' in fileData ? fileData.id : fileData.file.name + index;
            const isActive = withPreview && selectedFileId === key;
            return (
              <div
                key={key}
                className={clsx(styles['meta-data-row'], {
                  [styles['meta-data-row--active']]: isActive,
                })}
                onClick={(e) => {
                  // while editing a input element don't call fileClicked to aviod unessary opening filePreview
                  if (!(e.target instanceof HTMLInputElement)) {
                    onFileClicked?.(key, fileData);
                  }
                }}
              >
                <UploadFile
                  isNameEditable={isNameEditable}
                  fileData={fileData}
                  onRemoveFile={onRemoveFile}
                  uploadFile={uploadFile}
                  metaDataExtractionInput={metaDataExtractionInput}
                  onFileNameChange={onFileNameChange}
                />
              </div>
            );
          })}
        </div>
        {showPagination && files.length > pageSizes[0] && (
          <div
            className={styles['paginator__root']}
            data-testid="paginator-root"
          >
            <Paginator
              activePage={currentPage}
              activePaginationSize={currentPageSize}
              availablePaginationSizes={pageSizes}
              onPageChange={setCurrentPage}
              onPaginationSizeChange={(size) => {
                setCurrentPageSize(size);
                setCurrentPage(0);
              }}
              totalEntries={files.length}
            />
          </div>
        )}
      </div>
    </div>
  );
};
